import React, { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaSpinner } from 'react-icons/fa6';
import { TrainingDataSplitProps } from './types';
import { useDataSplit } from './hooks';
import { SplitTable } from './SplitTable';
import { getApiUrl } from '../../../../config/environment';
import { getSession } from '../../../../core/auth/supabase-auth';
import { downloadBlob } from '../../utils/downloadUtils';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

/**
 * TrainingDataSplit component
 * Allows configuration of train/validation/test data split percentages
 */
export const TrainingDataSplit: React.FC<TrainingDataSplitProps> = memo(({
  totalDataPoints,
  datasetCount,
  onSplitChange,
  disabled = false,
  sessionId
}) => {
  const { t } = useTranslation('training');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    trainPercentage,
    valPercentage,
    testPercentage,
    randomDat,
    calculatedCounts,
    isInvalidSum,
    isTrainEmpty,
    isValEmpty,
    totalPercentage,
    handleTrainPercentageChange,
    handleValPercentageChange,
    setRandomDat
  } = useDataSplit({ totalDataPoints, onSplitChange });

  const handleDownloadArrays = useCallback(async () => {
    if (!sessionId || isDownloading) return;
    setIsDownloading(true);
    try {
      const session = await getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const url = getApiUrl(`/api/training/download-arrays/${sessionId}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

      const blob = await response.blob();
      downloadBlob(blob, 'training_arrays.pkl.gz');
    } catch (error) {
      console.error('Failed to download training arrays:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [sessionId, isDownloading]);

  return (
    <div className="rounded-xl border border-border bg-card overflow-visible shadow-sm mb-4 sm:mb-6">
      <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-center gap-1.5">
          <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight">
            {t('training.dataSplit.title')}
          </h2>
          <InfoTooltip text={t('tooltips.datensatzAufteilung.section')} width="w-96" />
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t('training.dataSplit.totalDataPoints', { count: datasetCount ?? 0 })}
            </p>
            {sessionId && (
              <button
                type="button"
                onClick={handleDownloadArrays}
                disabled={isDownloading || disabled || !datasetCount}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150 shadow-sm"
                title={t('training.dataSplit.downloadArrays')}
              >
                {isDownloading ? (
                  <FaSpinner className="animate-spin text-[10px]" />
                ) : (
                  <FaDownload className="text-[10px]" />
                )}
                {isDownloading ? '...' : t('training.dataSplit.downloadArrays')}
              </button>
            )}
          </div>
          <SplitTable
            trainPercentage={trainPercentage}
            valPercentage={valPercentage}
            testPercentage={testPercentage}
            randomDat={randomDat}
            calculatedCounts={calculatedCounts}
            isInvalidSum={isInvalidSum}
            isTrainEmpty={isTrainEmpty}
            isValEmpty={isValEmpty}
            totalPercentage={totalPercentage}
            disabled={disabled}
            onTrainChange={handleTrainPercentageChange}
            onValChange={handleValPercentageChange}
            onRandomChange={setRandomDat}
          />
        </div>
      </div>
    </div>
  );
});

TrainingDataSplit.displayName = 'TrainingDataSplit';

export default TrainingDataSplit;
